import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import {
    ApiClientService,
    AuthenticationService,
    CachedValuesService,
    PermissionService,
    ToastService,
    RightPanelService
} from '@services/index';
import { UntilDestroy } from '@ngneat/until-destroy';
import { BehaviorSubject, map, Observable, take } from 'rxjs';
import { LocationForm } from './create-and-update-location-modal.model';
import { CreateLocationResponse } from '@services/api-client/responses';
import { CustomFormModule } from '@shared/custom-form/custom-form.module';
import { SharedModule } from '@shared/shared.module';
import { LocationModel } from 'app/types/location.type';
import { DropdownItem } from '@shared/components/dropdown/dropdown.component';
import { Tenant } from 'app/types';

@UntilDestroy()
@Component({
    selector: 'app-create-and-update-location-modal',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule, TranslateModule, CustomFormModule, SharedModule],
    templateUrl: './create-and-update-location-modal.component.html',
    styleUrl: './create-and-update-location-modal.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CreateAndUpdateLocationModalComponent implements OnInit {
    private readonly changeDetectorRef = inject(ChangeDetectorRef);
    private readonly translateService = inject(TranslateService);
    private readonly toastService = inject(ToastService);
    private readonly apiClientService = inject(ApiClientService);
    private readonly cachedValuesService = inject(CachedValuesService);
    private readonly authenticationService = inject(AuthenticationService);
    private readonly permissionService = inject(PermissionService);
    private readonly rightPanelService = inject(RightPanelService);

    @Input() public editLocation?: LocationModel;

    public showPotentialValidationMessages = false;
    public loading: boolean = false;
    public locationForm: FormGroup<LocationForm>;
    public isEditMode = false;
    public readonly isUnrestricted = this.permissionService.isUnrestricted();

    private readonly dataChangedSubject: BehaviorSubject<LocationModel | null> =
        new BehaviorSubject<LocationModel | null>(null);

    public readonly tenants$: Observable<DropdownItem<string>[]> = this.cachedValuesService.tenants$.pipe(
        map((data) =>
            data.map((item: Tenant) => {
                return {
                    label: item.name,
                    value: item.id
                } as DropdownItem<string>;
            })
        )
    );

    public get f() {
        return this.locationForm.controls;
    }

    public ngOnInit() {
        const isCreate = !this.editLocation;
        const fb = new FormBuilder();
        this.locationForm = fb.nonNullable.group({
            name: [this.editLocation?.name || '', [Validators.required]],
            tenantId: [null]
        });

        // Start in edit mode if creating new location, otherwise start in readonly mode
        this.isEditMode = isCreate;

        if (isCreate && this.isUnrestricted) {
            this.locationForm.controls.tenantId.setValidators(Validators.required);
        }
    }

    public showTenantSelection() {
        return this.isUnrestricted && !this.editLocation;
    }

    public enterEditMode() {
        this.isEditMode = true;
        this.changeDetectorRef.markForCheck();
    }

    public cancelEdit() {
        if (this.editLocation) {
            this.isEditMode = false;
            this.showPotentialValidationMessages = false;
            this.locationForm.patchValue({
                name: this.editLocation.name
            });
        } else {
            this.rightPanelService.hide();
        }
        this.changeDetectorRef.markForCheck();
    }

    public saveAndExitEdit() {
        this.showPotentialValidationMessages = true;
        this.changeDetectorRef.markForCheck();

        if (this.locationForm.invalid) {
            return;
        }

        this.loading = true;
        this.changeDetectorRef.markForCheck();

        const isCreate = !this.editLocation;

        const observable$ = isCreate
            ? this.apiClientService.createLocation$({
                  name: this.f.name.value,
                  tenantId: this.f.tenantId.value
              })
            : this.apiClientService.updateLocation$({
                  locationId: this.editLocation.id,
                  name: this.f.name.value
              });

        observable$.pipe(take(1)).subscribe({
            next: (response: CreateLocationResponse) => {
                this.loading = false;
                this.showPotentialValidationMessages = false;
                const title = this.translateService.instant('TOAST_TITLES.SUCCESS') as string;
                const message = this.translateService.instant(
                    isCreate
                        ? 'FEATURES.LOCATION.TOAST_MESSAGES.CREATE_SUCCESS'
                        : 'FEATURES.LOCATION.TOAST_MESSAGES.UPDATE_SUCCESS'
                ) as string;
                this.toastService.open(title, message, { type: 'success', progressBar: true });

                if (isCreate) {
                    this.dataChangedSubject.next(response);
                    this.editLocation = response;
                    this.isEditMode = false;
                    this.changeDetectorRef.markForCheck();
                    this.close();
                } else {
                    this.dataChangedSubject.next({ ...this.editLocation, name: this.f.name.value });
                    this.close();
                }
            },
            error: () => {
                this.loading = false;
                const title = this.translateService.instant('TOAST_TITLES.ERROR') as string;
                const message = this.translateService.instant(
                    isCreate
                        ? 'FEATURES.LOCATION.TOAST_MESSAGES.CREATE_ERROR'
                        : 'FEATURES.LOCATION.TOAST_MESSAGES.UPDATE_ERROR'
                ) as string;
                this.toastService.open(title, message, { type: 'error', progressBar: true });
                this.changeDetectorRef.markForCheck();
            }
        });
    }

    public close() {
        this.rightPanelService.hide();
    }
}
